import React, { useState } from 'react';
import { sortBy } from 'lodash';
import type { Mercury } from '@repo/types/Models';
import Message from './Message';

interface ConversationProps {
  nodes: Mercury.Models.SMS[] | undefined;
  hostNumber?: string;
  remoteNumber?: string;
}

const Conversation = ({ nodes, hostNumber, remoteNumber }: ConversationProps) => {
  const [showFilteredBody, setShowFilteredBody] = useState(true);

  const getMessageStream = () => {
    if (!nodes) return [];

    const multipart: Record<string, any[]> = {};
    let items: any[] = [];

    // Group multipart messages
    nodes.forEach((node) => {
      if (node.multipartId && node.udhCount && node.udhCount > 1) {
        const key = node.multipartId;
        if (!multipart[key]) {
          multipart[key] = [];
        }
        multipart[key].push(node);
      } else {
        items.push({
          ...node,
          nodeStatus: node.status,
          nodes: [node],
        });
      }
    });

    // Process multipart messages
    Object.keys(multipart).forEach((multipartId: string) => {
      let multipartNodes = multipart[multipartId];

      multipartNodes = sortBy(multipartNodes, (a) => a.udhSeqNum);

      const displayBody = multipartNodes.map(({ displayBody: db }: any) => db).join('');

      const node = multipartNodes[0];

      let nodeStatus = node.status;

      multipartNodes.forEach(({ status }: any) => {
        if (nodeStatus !== status) {
          nodeStatus = 'MIXED';
        }
      });

      items.push({
        ...node,
        id: node.multipartId || node.id,
        nodeStatus,
        displayBody,
        nodes: multipartNodes,
      });
    });

    // Sort by inserted date
    items = sortBy(items, (a) => a.insertedAt).reverse();

    const result: any[] = [];
    let lastDate: string | null = null;

    // Group by date
    items.forEach((node: any) => {
      const datetime = new Date(node.insertedAt);
      const newDate = datetime.toISOString().split('T')[0]; // Format as YYYY-MM-DD

      if (lastDate) {
        if (lastDate !== newDate) {
          lastDate = newDate;
          result.push({
            type: 'date',
            node: newDate,
            flags: {},
          });
        }
      } else {
        lastDate = newDate;
        result.push({
          type: 'date',
          node: newDate,
          flags: {},
        });
      }

      let hasFilteredMessage = false;
      node.nodes.forEach(({ isFilteredMessage }: { isFilteredMessage?: boolean }) => {
        hasFilteredMessage = hasFilteredMessage || !!isFilteredMessage;
      });

      result.push({
        type: 'message',
        node,
        flags: {
          hasFilteredMessage,
        },
      });
    });

    return result;
  };

  const nodeClass = (type: string, node: any) => {
    if (type === 'message') {
      return node.direction === 'INBOUND' ? 'justify-start' : 'justify-end';
    }
    return 'justify-around';
  };

  const messageStream = getMessageStream();

  return (
    <>
      {messageStream.length > 0 ? (
        <div className="p-5 border rounded-2xl border-grey-400 dark:border-grey-700 bg-grey-100 dark:bg-blue-1000">
          {/* Header with host/remote numbers */}
          <div className="flex flex-col justify-between sm:flex-row">
            {remoteNumber && (
              <div className="self-center">
                <div>Remote Number</div>
                <div className="font-mono">{remoteNumber}</div>
              </div>
            )}

            {hostNumber && (
              <div className="self-center mt-2 md:mt-0">
                <div>Host Number</div>
                <div className="font-mono">{hostNumber}</div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-5">
            {messageStream.map(({ type, node }, index) => (
              <div key={index} className={`flex flex-row ${nodeClass(type, node)}`}>
                {type === 'message' ? (
                  <Message node={node} />
                ) : type === 'date' ? (
                  <div className="w-full pb-2 text-center border-b border-grey-400 dark:border-grey-700">
                    {new Date(node).toLocaleDateString()}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Conversation;
